<template>
  <div class="h-auto overflow-auto flex flex-col">
    <woot-modal-header
      :header-title="headerTitle"
      :header-content="$t('QUEUES_MGMT.FORM.DESC')"
    />
    <form class="mx-0 flex flex-wrap" @submit.prevent="handleSubmit">
      <!-- Name -->
      <woot-input
        v-model.trim="name"
        :class="{ error: $v.name.$error }"
        class="w-full"
        :label="$t('QUEUES_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('QUEUES_MGMT.FORM.NAME.PLACEHOLDER')"
        :error="
          $v.name.$error ? $t('QUEUES_MGMT.FORM.NAME.REQUIRED_ERROR') : ''
        "
        @input="$v.name.$touch"
      />

      <!-- Primary group -->
      <!-- QM §5.10 — the multiselect -->
      <div class="w-full">
        <!-- QM §3.1.1(1) — the `.message` styling only exists as `.error .message`. -->
        <label :class="{ error: $v.primaryTeamId.$error }">
          {{ $t('QUEUES_MGMT.FORM.PRIMARY_GROUP.LABEL') }}
          <multiselect
            v-model="selectedPrimaryTeam"
            :options="teams"
            track-by="id"
            label="name"
            :close-on-select="true"
            :allow-empty="false"
            selected-label
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            deselect-label=""
            :placeholder="$t('QUEUES_MGMT.FORM.PRIMARY_GROUP.PLACEHOLDER')"
            @input="$v.primaryTeamId.$touch"
          />
          <span v-if="$v.primaryTeamId.$error" class="message">
            {{ $t('QUEUES_MGMT.FORM.PRIMARY_GROUP.REQUIRED_ERROR') }}
          </span>
        </label>
      </div>

      <!-- Overflow group -->
      <div class="w-full">
        <!-- QM §3.1.1(5), §3.1.1(6) — wrapping requirement as the primary group. -->
        <label :class="{ error: $v.overflowTeamId.$error }">
          {{ $t('QUEUES_MGMT.FORM.OVERFLOW_GROUP.LABEL') }}
          <multiselect
            v-model="selectedOverflowTeam"
            :options="overflowTeamOptions"
            track-by="id"
            label="name"
            :close-on-select="true"
            :allow-empty="true"
            selected-label
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
            :placeholder="$t('QUEUES_MGMT.FORM.OVERFLOW_GROUP.NONE')"
            @input="$v.overflowTeamId.$touch"
          />
          <span v-if="$v.overflowTeamId.$error" class="message">
            {{ $t('QUEUES_MGMT.FORM.OVERFLOW_GROUP.REQUIRED_ERROR') }}
          </span>
        </label>
      </div>

      <!-- Time-based overflow expansion -->
      <div class="w-full mt-2">
        <label class="toggle-row">
          <input
            v-model="timeEnabled"
            type="checkbox"
            :value="true"
            @change="touchDependents('overflowTeamId', 'timeMinutes')"
          />
          {{ $t('QUEUES_MGMT.FORM.TIME_TRIGGER.LABEL') }}
        </label>
        <woot-input
          v-if="timeEnabled"
          v-model.number="timeMinutes"
          type="number"
          min="1"
          :class="{ error: $v.timeMinutes.$error }"
          class="w-full"
          :label="$t('QUEUES_MGMT.FORM.TIME_TRIGGER.MINUTES_LABEL')"
          :error="
            $v.timeMinutes.$error
              ? $t('QUEUES_MGMT.FORM.TIME_TRIGGER.ERROR')
              : ''
          "
          @input="$v.timeMinutes.$touch"
        />
      </div>

      <!-- SLA-based overflow expansion -->
      <div class="w-full mt-2">
        <label class="toggle-row">
          <input
            v-model="slaEnabled"
            type="checkbox"
            :value="true"
            @change="touchDependents('overflowTeamId', 'slaPercent')"
          />
          {{ $t('QUEUES_MGMT.FORM.SLA_TRIGGER.LABEL') }}
        </label>
        <woot-input
          v-if="slaEnabled"
          v-model.number="slaPercent"
          type="number"
          min="1"
          max="100"
          :class="{ error: $v.slaPercent.$error }"
          class="w-full"
          :label="$t('QUEUES_MGMT.FORM.SLA_TRIGGER.PERCENT_LABEL')"
          :error="
            $v.slaPercent.$error ? $t('QUEUES_MGMT.FORM.SLA_TRIGGER.ERROR') : ''
          "
          @input="$v.slaPercent.$touch"
        />
      </div>

      <!-- Auto-return on inactivity -->
      <div class="w-full mt-2">
        <label class="toggle-row">
          <input
            v-model="inactivityEnabled"
            type="checkbox"
            :value="true"
            @change="touchDependents('inactivityMinutes')"
          />
          {{ $t('QUEUES_MGMT.FORM.INACTIVITY.LABEL') }}
        </label>
        <woot-input
          v-if="inactivityEnabled"
          v-model.number="inactivityMinutes"
          type="number"
          min="1"
          :class="{ error: $v.inactivityMinutes.$error }"
          class="w-full"
          :label="$t('QUEUES_MGMT.FORM.INACTIVITY.MINUTES_LABEL')"
          :error="
            $v.inactivityMinutes.$error
              ? $t('QUEUES_MGMT.FORM.INACTIVITY.ERROR')
              : ''
          "
          @input="$v.inactivityMinutes.$touch"
        />
      </div>

      <div class="flex justify-end items-center py-2 px-0 gap-2 w-full">
        <woot-button class="button clear" @click.prevent="onClose">
          {{ $t('QUEUES_MGMT.FORM.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :is-disabled="$v.$invalid || submitInProgress"
          :is-loading="submitInProgress"
        >
          {{ submitButtonText }}
        </woot-button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  required,
  minValue,
  between,
  requiredIf,
} from 'vuelidate/lib/validators';

export default {
  props: {
    onSubmit: {
      type: Function,
      default: () => {},
    },
    submitInProgress: {
      type: Boolean,
      default: false,
    },
    formData: {
      type: Object,
      default: () => ({}),
    },
    submitButtonText: {
      type: String,
      default: '',
    },
    headerTitle: {
      type: String,
      default: '',
    },
  },
  data() {
    const q = this.formData || {};
    return {
      name: q.name || '',
      primaryTeamId: q.primary_team_id || null,
      overflowTeamId: q.overflow_team_id || null,
      timeEnabled: q.overflow_time_trigger_enabled || false,
      timeMinutes: q.overflow_time_trigger_seconds
        ? q.overflow_time_trigger_seconds / 60
        : null,
      slaEnabled: q.overflow_sla_trigger_enabled || false,
      slaPercent: q.overflow_sla_trigger_percent || null,
      inactivityEnabled: q.inactivity_auto_return_enabled || false,
      inactivityMinutes: q.inactivity_timeout_seconds
        ? q.inactivity_timeout_seconds / 60
        : null,
    };
  },
  validations: {
    name: { required },
    primaryTeamId: { required },
    // QM §3.1.1 — overflow group is required whenever either expansion trigger is on.
    overflowTeamId: {
      required: requiredIf(function needsOverflow() {
        return this.timeEnabled || this.slaEnabled;
      }),
    },
    timeMinutes: {
      required: requiredIf('timeEnabled'),
      minValue: minValue(1),
    },
    slaPercent: {
      required: requiredIf('slaEnabled'),
      between: between(1, 100),
    },
    inactivityMinutes: {
      required: requiredIf('inactivityEnabled'),
      minValue: minValue(1),
    },
  },
  computed: {
    ...mapGetters({
      teams: 'teams/getTeams',
    }),
    // QM §3.1.1 Enforce overflowGroupId ≠ primaryGroupId at the UI level.
    overflowTeamOptions() {
      return this.teams.filter(team => team.id !== this.primaryTeamId);
    },
    // multiselect binds to the team object; keep the *_TeamId ids as source of truth.
    selectedPrimaryTeam: {
      get() {
        return this.teams.find(team => team.id === this.primaryTeamId) || null;
      },
      set(team) {
        this.primaryTeamId = team ? team.id : null;
      },
    },
    selectedOverflowTeam: {
      get() {
        return this.teams.find(team => team.id === this.overflowTeamId) || null;
      },
      set(team) {
        this.overflowTeamId = team ? team.id : null;
      },
    },
  },
  watch: {
    primaryTeamId(newVal) {
      if (this.overflowTeamId === newVal) {
        this.overflowTeamId = null;
      }
    },
  },
  mounted() {
    if (!this.teams.length) {
      this.$store.dispatch('teams/get');
    }
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    // QM §3.1.1(5), §3.1.1(6), §3.1.1(7)
    touchDependents(...fields) {
      fields.forEach(field => this.$v[field].$touch());
    },
    handleSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.onSubmit({
        name: this.name,
        primary_team_id: this.primaryTeamId,
        overflow_team_id: this.overflowTeamId,
        overflow_time_trigger_enabled: this.timeEnabled,
        overflow_time_trigger_seconds: this.timeEnabled
          ? this.timeMinutes * 60
          : null,
        overflow_sla_trigger_enabled: this.slaEnabled,
        overflow_sla_trigger_percent: this.slaEnabled ? this.slaPercent : null,
        inactivity_auto_return_enabled: this.inactivityEnabled,
        inactivity_timeout_seconds: this.inactivityEnabled
          ? this.inactivityMinutes * 60
          : null,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import '~dashboard/assets/scss/variables';

select {
  @apply w-full;
}

.toggle-row {
  @apply flex items-center gap-2;

  input {
    @apply m-0;
  }
}
</style>
